import {
  IDynamicItem,
  isIDynamicItem,
  PartialConfig,
  PartialIDynamicItem,
  WithMergeMethod,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { ensureArray } from '@epicuro-next/utilities';
import * as deepmerge from 'deepmerge';

type JsonObject = {
  // tslint:disable-next-line:no-any
  [key: string]: any;
};

function deepClone(config: PartialConfig): PartialConfig {
  return JSON.parse(JSON.stringify(config));
}

export function mergeConfigs(
  configA: PartialConfig,
  configB: PartialConfig,
): PartialIDynamicItem[] {
  return mergeInternal(deepClone(configA), deepClone(configB));
}

export function mergeMultipleConfigs(
  configA: PartialIDynamicItem,
  configsB: PartialIDynamicItem[],
): PartialIDynamicItem[] {
  let mergedConfig: PartialIDynamicItem[] = [configA];
  for (const config of configsB) {
    mergedConfig = mergeConfigs(mergedConfig, config);
  }
  return mergedConfig;
}

export function allEntriesAreIDynamicItems(
  items: PartialIDynamicItem[],
  invalidItems?: PartialIDynamicItem[],
): items is IDynamicItem[] {
  if (items === undefined || items === null) {
    return false;
  }

  for (const item of items) {
    if (!isIDynamicItem(item)) {
      invalidItems?.push(item);
      return false;
    }

    if (
      item.children &&
      !allEntriesAreIDynamicItems(item.children, invalidItems)
    ) {
      return false;
    }
  }

  return true;
}

function mergeInternal(
  configA: PartialConfig,
  configB: PartialConfig,
  itemsFromBThatHasBeenMerged?: PartialIDynamicItem[],
): PartialIDynamicItem[] {
  configA = ensureArray(configA);
  configB = ensureArray(configB);
  itemsFromBThatHasBeenMerged ??= [];

  const mergedConfig = mergeObjectsFromBIntoAWithSameId(
    configA,
    configB,
    itemsFromBThatHasBeenMerged,
  ) as PartialIDynamicItem[];

  addItemsFromConfigBIfNotMergedYet(
    configB,
    itemsFromBThatHasBeenMerged,
    mergedConfig,
  );
  return mergedConfig;
}

function addItemsFromConfigBIfNotMergedYet(
  configB: PartialIDynamicItem[],
  itemsFromBThatHasBeenMerged: PartialIDynamicItem[],
  mergedConfig: PartialIDynamicItem[],
) {
  for (const item of configB) {
    const itemHasNotBeenMergedYet = !itemsFromBThatHasBeenMerged.includes(item);
    const itemHasId = item.id !== undefined && item.id !== null;
    if (itemHasNotBeenMergedYet && (isIDynamicItem(item) || itemHasId)) {
      mergedConfig.push(item);
    }
  }
}

export function findObjectsById(
  jsonObject: JsonObject,
  id: string,
): JsonObject[] {
  const result: JsonObject[] = [];

  function findObjectById(obj: JsonObject) {
    if (obj.id === id) {
      result.push(obj);
    }

    for (const key in obj) {
      if (obj[key] && typeof obj[key] === 'object') {
        findObjectById(obj[key]);
      }
    }
  }

  findObjectById(jsonObject);

  return result;
}

export function mergeObjectsFromBIntoAWithSameId(
  treeA: unknown,
  treeB: unknown,
  itemsFromTreeBThatHasBeenMerged?: unknown[],
): unknown {
  if (!treeA || !treeB) {
    return treeA;
  }

  if (Array.isArray(treeA)) {
    return mergeObjectsFromBIntoAStartingWithArray(
      treeA,
      treeB,
      itemsFromTreeBThatHasBeenMerged,
    );
  } else if (isObject(treeA)) {
    treeA = mergeObjectsFromBIntoA(
      treeA,
      treeB,
      itemsFromTreeBThatHasBeenMerged,
    );
  }

  return treeA;
}

function mergeObjectsFromBIntoAStartingWithArray(
  treeA: unknown[],
  treeB: {},
  itemsFromTreeBThatHasBeenMerged: unknown[] | undefined,
) {
  const arrayWithMergedObjects: unknown[] = [];
  for (const item of treeA) {
    const mergedItem = mergeObjectsFromBIntoAWithSameId(
      item,
      treeB,
      itemsFromTreeBThatHasBeenMerged,
    );
    arrayWithMergedObjects.push(mergedItem);
  }
  return arrayWithMergedObjects;
}

function mergeObjectsFromBIntoA(
  treeA: unknown,
  treeB: {},
  itemsFromTreeBThatHasBeenMerged: unknown[] | undefined,
) {
  const id = (treeA as { id?: string }).id;
  const hasId = id !== undefined && id !== null && id.length > 0;

  if (hasId) {
    const objectsInBWithSameId = findObjectsById(treeB, id);
    for (const objectInBWithSameId of objectsInBWithSameId) {
      const mergeMethod = (objectInBWithSameId as WithMergeMethod).mergeMethod;
      if (mergeMethod) {
        const mergeMethodWrapper =
          String.raw`
          const f = ` +
          mergeMethod +
          `;
          return f(a, b);
        `.trim();

        const mergeResult = Function(
          'a',
          'b',
          mergeMethodWrapper,
        )(treeA, objectInBWithSameId);
        treeA = mergeResult;
      } else {
        treeA = deepmerge(treeA as object, objectInBWithSameId, {
          arrayMerge: (_, source) => source,
        });
      }
      itemsFromTreeBThatHasBeenMerged?.push(objectInBWithSameId);
    }
  }

  const keys = Object.keys(treeA as object);
  for (const key of keys) {
    const value = (treeA as { [key: string]: unknown })[key];
    if (Boolean(value) && typeof value === 'object') {
      (treeA as { [key: string]: unknown })[key] =
        mergeObjectsFromBIntoAWithSameId(
          value,
          treeB,
          itemsFromTreeBThatHasBeenMerged,
        );
    }
  }
  return treeA;
}

function isObject(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}
